import React, { useEffect, useState } from 'react'
import {
  Layout, Card, Button, Segmented, Input, Checkbox, Tag, Avatar, Empty, Tabs,
  Form, Upload, DatePicker, Switch, Select, Space, Divider, Alert, Badge, InputNumber, message
} from 'antd'
import {
  UploadOutlined, UserOutlined, VideoCameraOutlined, PictureOutlined,
  ClockCircleOutlined, SendOutlined, SearchOutlined, FolderOutlined
} from '@ant-design/icons'
import api from '../api'

const { Sider, Content } = Layout
const { TextArea } = Input
const { RangePicker } = DatePicker

const PLATFORM_COLORS = {
  douyin: '#ff4d4f', kuaishou: '#fa8c16', xiaohongshu: '#eb2f96',
  weibo: '#1890ff', bilibili: '#13c2c2', shipinhao: '#52c41a', jinritoutiao: '#722ed1'
}

const PLATFORM_NAMES = {
  douyin: '抖音', kuaishou: '快手', xiaohongshu: '小红书',
  weibo: '微博', bilibili: 'B站', shipinhao: '视频号', jinritoutiao: '头条'
}

const STATUS_TEXT = {
  online: '在线', offline: '掉线', pending: '待扫码'
}

const PREVIEW_ACCOUNTS = []

const PREVIEW_GROUPS = [
  { group_id: 'default', name: '默认分组' },
  { group_id: 'brand', name: '品牌矩阵' }
]

const PLATFORM_FIELDS = {
  douyin: ['位置', '封面方式'],
  kuaishou: ['标题覆盖', '话题', '可见范围'],
  shipinhao: ['描述覆盖', '位置', '原创声明'],
  xiaohongshu: ['标题覆盖', '正文覆盖', '话题', '图片排序'],
  weibo: ['正文覆盖', '话题', '评论权限'],
  bilibili: ['标题覆盖', '分区', '标签', '创作声明'],
  jinritoutiao: ['标题覆盖', '摘要', '分类']
}

const DOUYIN_COVER_MODE_OPTIONS = [
  { value: 'ai_recommend', label: 'Ai智能推荐封面' }
]

const BILIBILI_SECTION_OPTIONS = [
  { value: '动画', label: '动画' }, { value: '音乐', label: '音乐' }, { value: '舞蹈', label: '舞蹈' },
  { value: '游戏', label: '游戏' }, { value: '知识', label: '知识' }, { value: '科技数码', label: '科技数码' },
  { value: '生活', label: '生活' }, { value: '美食', label: '美食' }, { value: '动物圈', label: '动物圈' },
  { value: '鬼畜', label: '鬼畜' }, { value: '时尚', label: '时尚' }, { value: '运动', label: '运动' },
  { value: '娱乐', label: '娱乐' }, { value: '综艺', label: '综艺' }, { value: '影视', label: '影视' },
  { value: '纪录片', label: '纪录片' }, { value: '电影', label: '电影' }, { value: '电视剧', label: '电视剧' },
  { value: '资讯', label: '资讯' }, { value: '汽车', label: '汽车' }, { value: '搞笑', label: '搞笑' },
  { value: '日常', label: '日常' }, { value: 'vlog', label: 'vlog' }, { value: '人工智能', label: '人工智能' },
  { value: '绘画', label: '绘画' }, { value: '手工', label: '手工' }, { value: '三农', label: '三农' },
  { value: '动物', label: '动物' }, { value: '亲子', label: '亲子' }, { value: '健康', label: '健康' },
  { value: '情感', label: '情感' }, { value: '旅游出行', label: '旅游出行' }, { value: '小剧场', label: '小剧场' },
  { value: '生活兴趣', label: '生活兴趣' }, { value: '生活经验', label: '生活经验' }
]

const BILIBILI_DECLARATION_OPTIONS = [
  { value: '内容无需标注', label: '内容无需标注' },
  { value: '含AI生成内容', label: '含AI生成内容' },
  { value: '含虚构/演绎内容', label: '含虚构/演绎内容' },
  { value: '内容含营销信息', label: '内容含营销信息' },
  { value: '个人观点，仅供参考', label: '个人观点，仅供参考' },
  { value: '内容为转载', label: '内容为转载' },
  { value: '内容为自制：禁止转载', label: '内容为自制：未经允许禁止转载' }
]

const REQUIRED_PLATFORM_FIELDS = {
  bilibili: ['分区', '标签', '创作声明']
}

const IMAGE_TEXT_DISABLED_PLATFORMS = ['shipinhao', 'jinritoutiao', 'bilibili']

function getAuthUser() {
  try { return JSON.parse(sessionStorage.getItem('auth_user') || 'null') } catch { return null }
}

function hasMultiPublishAccess(user) {
  if (!user) return true
  if (user.role === 'admin') return true
  return !['basic', ''].includes(String(user.membership_level || user.level || 'basic'))
}

function PublishPage() {
  const [publishType, setPublishType] = useState('video')
  const [publishMode, setPublishMode] = useState('single')
  const [accounts, setAccounts] = useState([])
  const [groups, setGroups] = useState([])
  const [selectedIds, setSelectedIds] = useState([])
  const [selectedGroupIds, setSelectedGroupIds] = useState([])
  const [expandedGroupIds, setExpandedGroupIds] = useState([])
  const [keyword, setKeyword] = useState('')
  const [scheduleRange, setScheduleRange] = useState(null)
  const [timeRanges, setTimeRanges] = useState('09:20-11:50,13:50-15:00,18:00-22:00')
  const [scheduleInterval, setScheduleInterval] = useState(30)
  const [generatedTimes, setGeneratedTimes] = useState('')
  const [sourceFolderPath, setSourceFolderPath] = useState('')
  const [platformSourceFolders, setPlatformSourceFolders] = useState({})
  const [platformAfterPublishActions, setPlatformAfterPublishActions] = useState({})
  const [platformMoveTargetFolders, setPlatformMoveTargetFolders] = useState({})
  const [moveTargetFolderPath, setMoveTargetFolderPath] = useState('')
  const [afterPublishAction, setAfterPublishAction] = useState('move')
  const [titlePool, setTitlePool] = useState('')
  const [titleOrder, setTitleOrder] = useState('sequential')
  const [bodyPool, setBodyPool] = useState('')
  const [bodyOrder, setBodyOrder] = useState('sequential')
  const [topicPool, setTopicPool] = useState('')
  const [topicOrder, setTopicOrder] = useState('random')
  const [topicPickCount, setTopicPickCount] = useState(3)
  const [locationPool, setLocationPool] = useState('')
  const [locationOrder, setLocationOrder] = useState('sequential')
  const [douyinCoverMode, setDouyinCoverMode] = useState('ai_recommend')
  const [commentPool, setCommentPool] = useState('')
  const [commentOrder, setCommentOrder] = useState('random')
  const [commentDelayMinutes, setCommentDelayMinutes] = useState(10)
  const [commentCount, setCommentCount] = useState(1)
  const [commentIntervalMinutes, setCommentIntervalMinutes] = useState(5)
  const [showPublishBrowser, setShowPublishBrowser] = useState(false)
  const [singleScheduleEnabled, setSingleScheduleEnabled] = useState(false)
  const [singleScheduleTime, setSingleScheduleTime] = useState(null)
  const [authUser, setAuthUser] = useState(getAuthUser)

  useEffect(() => {
    let cancelled = false
    Promise.all([api.getAccounts(), api.getGroups()]).then(([list, groupList]) => {
      if (cancelled) return
      const nextAccounts = Array.isArray(list) && list.length > 0 ? list : PREVIEW_ACCOUNTS
      const nextGroups = Array.isArray(groupList) && groupList.length > 0 ? groupList : PREVIEW_GROUPS
      setAccounts(nextAccounts)
      setGroups(nextGroups)
      setSelectedIds(nextAccounts.filter(item => item.status === 'online').slice(0, 4).map(item => item.account_id))
    }).catch(() => {
      if (!cancelled) {
        setAccounts(PREVIEW_ACCOUNTS)
        setGroups(PREVIEW_GROUPS)
        setSelectedIds(PREVIEW_ACCOUNTS.filter(item => item.status === 'online').map(item => item.account_id))
      }
    })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (publishType !== 'image_text') return
    setSelectedIds(prev => prev.filter(accountId => {
      const account = accounts.find(item => item.account_id === accountId)
      return account && !IMAGE_TEXT_DISABLED_PLATFORMS.includes(account.platform)
    }))
    setSelectedGroupIds(prev => prev.filter(groupId => accounts.some(item => String(item.group_id || 'default') === String(groupId) && !IMAGE_TEXT_DISABLED_PLATFORMS.includes(item.platform))))
  }, [publishType, accounts])

  const selectedAccounts = accounts.filter(item => selectedIds.includes(item.account_id))
  const selectedPlatforms = Array.from(new Set(selectedAccounts.map(item => item.platform)))
  const isBasicUser = !hasMultiPublishAccess(authUser)
  const lockedPublishPlatform = String(authUser?.locked_publish_platform || '').trim()
  const currentBasicPlatform = lockedPublishPlatform || (isBasicUser && selectedPlatforms.length === 1 ? selectedPlatforms[0] : '')

  useEffect(() => {
    if (!isBasicUser || accounts.length === 0) return
    const targetPlatform = lockedPublishPlatform || accounts.find(account => selectedIds.includes(account.account_id))?.platform || ''
    if (!targetPlatform) return
    setSelectedIds(prev => {
      const next = prev.filter(accountId => accounts.find(account => account.account_id === accountId)?.platform === targetPlatform)
      return next.length === prev.length && next.every((id, index) => id === prev[index]) ? prev : next
    })
    setSelectedGroupIds(prev => prev.length === 0 ? prev : [])
  }, [isBasicUser, lockedPublishPlatform, accounts, selectedIds])

  const selectedGroup = selectedGroupIds.length === 1 ? groups.find(item => item.group_id === selectedGroupIds[0] || item.id === selectedGroupIds[0]) : null
  const canInheritLastSettings = (selectedGroupIds.length === 1 && selectedIds.length === accounts.filter(item => String(item.group_id || 'default') === String(selectedGroupIds[0])).length) || (selectedGroupIds.length === 0 && selectedIds.length === 1)
  const settingSourceText = selectedGroup ? `当前单独选择分组「${selectedGroup.name || selectedGroup.group_name || '未命名分组'}」，可以继承该分组上次发布设置。` : '当前单独选择 1 个账号，可以继承该账号上次发布设置。'
  const filteredAccounts = accounts.filter(item => {
    const value = `${item.nickname || ''}${PLATFORM_NAMES[item.platform] || ''}${item.remark || ''}`.toLowerCase()
    return value.includes(keyword.trim().toLowerCase())
  })
  const standaloneAccounts = groups.length > 0 ? filteredAccounts.filter(item => !groups.some(group => String(group.group_id || group.id || 'default') === String(item.group_id || 'default'))) : filteredAccounts

  const isAccountDisabled = (account) => {
    if (publishType === 'image_text' && IMAGE_TEXT_DISABLED_PLATFORMS.includes(account.platform)) return true
    if (!isBasicUser) return false
    if (lockedPublishPlatform) return account.platform !== lockedPublishPlatform
    if (currentBasicPlatform) return account.platform !== currentBasicPlatform
    return false
  }

  const getAccountDisabledReason = (account) => {
    if (publishType === 'image_text' && IMAGE_TEXT_DISABLED_PLATFORMS.includes(account.platform)) return '不支持图文'
    if (!isBasicUser) return ''
    if (lockedPublishPlatform && account.platform !== lockedPublishPlatform) return `基础版已绑定${PLATFORM_NAMES[lockedPublishPlatform] || lockedPublishPlatform}`
    if (currentBasicPlatform && account.platform !== currentBasicPlatform) return `基础版本次只能选择${PLATFORM_NAMES[currentBasicPlatform] || currentBasicPlatform}`
    return ''
  }

  const getSettingScope = () => {
    if (selectedGroupIds.length === 1 && selectedIds.length === accounts.filter(item => String(item.group_id || 'default') === String(selectedGroupIds[0])).length) {
      return { scope: 'group', id: String(selectedGroupIds[0]) }
    }
    if (selectedGroupIds.length === 0 && selectedIds.length === 1) {
      return { scope: 'account', id: selectedIds[0] }
    }
    return null
  }

  useEffect(() => {
    const settingScope = getSettingScope()
    if (!settingScope) return
    api.getPublishSettings(settingScope.scope, settingScope.id).then(settings => {
      if (!settings) return
      setSourceFolderPath(settings.sourceFolderPath || '')
      setMoveTargetFolderPath(settings.moveTargetFolderPath || '')
      setAfterPublishAction(settings.afterPublishAction || 'move')
      setTimeRanges(settings.timeRanges || '09:20-11:50,13:50-15:00,18:00-22:00')
      setScheduleInterval(Number(settings.scheduleInterval || 30))
      setGeneratedTimes('')
      setTitlePool(settings.titlePool || '')
      setTitleOrder(settings.titleOrder || 'sequential')
      setBodyPool(settings.bodyPool || '')
      setBodyOrder(settings.bodyOrder || 'sequential')
      setTopicPool(settings.topicPool || '')
      setTopicOrder(settings.topicOrder || 'random')
      setTopicPickCount(Number(settings.topicPickCount || 3))
      setLocationPool(settings.locationPool || '')
      setLocationOrder(settings.locationOrder || 'sequential')
      setDouyinCoverMode(settings.douyinCoverMode || 'ai_recommend')
      setCommentPool(settings.commentPool || '')
      setCommentOrder(settings.commentOrder || 'random')
      setCommentDelayMinutes(Number(settings.commentDelayMinutes || 10))
      setCommentCount(Number(settings.commentCount || 1))
      setCommentIntervalMinutes(Number(settings.commentIntervalMinutes || 5))
      setShowPublishBrowser(Boolean(settings.showPublishBrowser))
    })
  }, [selectedIds, selectedGroupIds, accounts])

  const toggleAccount = (account) => {
    if (isAccountDisabled(account)) {
      const reason = getAccountDisabledReason(account)
      if (reason) message.warning(reason)
      return
    }
    setSelectedGroupIds(prev => prev.filter(groupId => String(groupId) !== String(account.group_id || 'default')))
    if (account.status === 'offline') {
      setSelectedIds(prev => prev.includes(account.account_id) ? prev.filter(id => id !== account.account_id) : [...prev, account.account_id])
      return
    }
    setSelectedIds(prev => prev.includes(account.account_id) ? prev.filter(id => id !== account.account_id) : [...prev, account.account_id])
  }

  const toggleGroup = (group) => {
    const groupId = String(group.group_id || group.id || 'default')
    const groupAccountIds = accounts.filter(item => String(item.group_id || 'default') === groupId && !isAccountDisabled(item)).map(item => item.account_id)
    if (groupAccountIds.length === 0) return
    const checked = selectedGroupIds.includes(groupId)
    setSelectedGroupIds(prev => checked ? prev.filter(id => id !== groupId) : [...prev, groupId])
    setSelectedIds(prev => checked ? prev.filter(id => !groupAccountIds.includes(id)) : Array.from(new Set([...prev, ...groupAccountIds])))
  }

  const toggleGroupExpanded = (groupId) => {
    setExpandedGroupIds(prev => prev.includes(groupId) ? prev.filter(id => id !== groupId) : [...prev, groupId])
  }

  const renderGroup = (group) => {
    const groupId = String(group.group_id || group.id || 'default')
    const groupAccounts = accounts.filter(item => String(item.group_id || 'default') === groupId)
    const checked = selectedGroupIds.includes(groupId)
    const expanded = expandedGroupIds.includes(groupId)
    return (
      <React.Fragment key={groupId}>
        <div
          onClick={() => toggleGroup(group)}
          style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0', background: checked ? '#d9f7be' : '#fafafa' }}
        >
          <Checkbox checked={checked} onChange={() => toggleGroup(group)} onClick={event => event.stopPropagation()} />
          <Avatar icon={<FolderOutlined />} style={{ background: '#1677ff' }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>{group.name || group.group_name || '未命名分组'}</div>
            <div style={{ fontSize: 12, color: '#999' }}>分组内 {groupAccounts.length} 个账号</div>
          </div>
          <Button size="small" type="link" onClick={event => { event.stopPropagation(); toggleGroupExpanded(groupId) }}>{expanded ? '收起' : '展开'}</Button>
          <Tag color="blue">分组</Tag>
        </div>
        {expanded && (
          <div style={{ background: '#fbfbfb', borderBottom: '1px solid #f0f0f0' }}>
            {groupAccounts.map(account => renderAccount(account, true))}
          </div>
        )}
      </React.Fragment>
    )
  }

  const renderAccount = (account, nested = false) => {
    const checked = selectedIds.includes(account.account_id)
    const platformName = PLATFORM_NAMES[account.platform] || account.platform
    const disabled = isAccountDisabled(account)
    const disabledReason = getAccountDisabledReason(account)
    const isOnline = account.status === 'online'
    const statusColor = isOnline ? '#52c41a' : account.status === 'offline' ? '#faad14' : '#d9d9d9'
    return (
      <div
        key={`${nested ? 'group' : 'list'}-${account.account_id}`}
        onClick={() => toggleAccount(account)}
        style={{
          display: 'flex', gap: 10, alignItems: 'center', padding: nested ? '9px 12px 9px 34px' : '10px 12px', cursor: disabled ? 'not-allowed' : 'pointer',
          borderBottom: '1px solid #f0f0f0', background: checked ? '#e6f4ff' : '#fff', opacity: disabled ? 0.45 : (isOnline ? 1 : 0.6)
        }}
      >
        <Checkbox disabled={disabled} checked={checked} onChange={() => toggleAccount(account)} onClick={event => event.stopPropagation()} />
        <Badge dot color={statusColor} offset={[-2, 30]}>
          <Avatar src={account.avatar_url} icon={<UserOutlined />}
            style={{ background: PLATFORM_COLORS[account.platform] || '#999', filter: isOnline ? 'none' : 'grayscale(100%)' }} />
        </Badge>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: isOnline ? '#000' : '#999' }}>
            {account.nickname || account.remark || platformName}
          </div>
          <div style={{ fontSize: 12, color: '#999', display: 'flex', gap: 6, alignItems: 'center' }}>
            <span>{platformName}</span>
            <span>{account.fans_text ? `粉丝 ${account.fans_text}` : STATUS_TEXT[account.status] || '未知'}</span>
          </div>
        </div>
        <Tag color={disabled ? 'default' : isOnline ? 'success' : 'warning'}>{disabled ? disabledReason : STATUS_TEXT[account.status] || '未知'}</Tag>
      </div>
    )
  }

  const renderCommonSettings = () => (
    <div style={{ padding: 4 }}>
      <Alert
        type={canInheritLastSettings ? 'success' : 'warning'}
        showIcon
        message={canInheritLastSettings ? settingSourceText : '当前选择了多个账号或多个分组，设置项默认空白，需要为本次发布重新填写。'}
        style={{ marginBottom: 16 }}
      />
      <Alert
        type="info"
        showIcon
        message="通用设置会作为所有平台的默认发布内容，平台标签中填写的字段会覆盖通用内容。"
        style={{ marginBottom: 16 }}
      />
      <Form layout="vertical">
        <Form.Item label={publishType === 'video' ? '视频文件' : '图片素材'}>
          <Upload.Dragger multiple={publishType !== 'video'} beforeUpload={() => false} style={{ padding: 8 }}>
            <p><UploadOutlined style={{ fontSize: 24, color: '#1677ff' }} /></p>
            <p>{publishType === 'video' ? '点击或拖拽上传视频' : '点击或拖拽上传图片组'}</p>
            <p style={{ color: '#999', fontSize: 12 }}>当前为界面预览，真实发布执行将在下一阶段接入</p>
          </Upload.Dragger>
        </Form.Item>
        <Form.Item label="通用标题">
          <Input placeholder="输入所有平台默认使用的标题" />
        </Form.Item>
        <Form.Item label="通用正文">
          <TextArea rows={5} placeholder="输入所有平台默认使用的正文、描述或文案" />
        </Form.Item>
        <Form.Item label="封面">
          <Upload beforeUpload={() => false} maxCount={1}>
            <Button icon={<UploadOutlined />}>选择封面图</Button>
          </Upload>
        </Form.Item>
        <Space size="large" wrap>
          <Form.Item label="定时发布" style={{ marginBottom: 0 }}>
            <Switch checked={singleScheduleEnabled} onChange={setSingleScheduleEnabled} checkedChildren="开启" unCheckedChildren="关闭" />
          </Form.Item>
          {singleScheduleEnabled && (
            <Form.Item label="发布时间" style={{ marginBottom: 0 }}>
              <DatePicker showTime value={singleScheduleTime} onChange={setSingleScheduleTime} placeholder="选择发布时间" />
            </Form.Item>
          )}
        </Space>
      </Form>
    </div>
  )

  const buildScheduleTimes = () => {
    if (!scheduleRange || scheduleRange.length !== 2) {
      return []
    }
    const start = scheduleRange[0].startOf('day')
    const end = scheduleRange[1].startOf('day')
    const minTime = new Date(Date.now() + 2 * 60 * 60 * 1000)
    const ranges = timeRanges.split(',').map(item => item.trim()).filter(Boolean)
    const result = []
    let current = start
    while (current.valueOf() <= end.valueOf()) {
      ranges.forEach(range => {
        const [startText, endText] = range.split('-').map(item => item && item.trim())
        if (!startText || !endText) return
        const [startHour, startMinute] = startText.split(':').map(Number)
        const [endHour, endMinute] = endText.split(':').map(Number)
        if ([startHour, startMinute, endHour, endMinute].some(Number.isNaN)) return
        let cursor = current.hour(startHour).minute(startMinute).second(0)
        const limit = current.hour(endHour).minute(endMinute).second(0)
        while (cursor.valueOf() <= limit.valueOf()) {
          if (cursor.toDate() > minTime) result.push(cursor.format('YYYY-MM-DD HH:mm'))
          cursor = cursor.add(scheduleInterval || 30, 'minute')
        }
      })
      current = current.add(1, 'day')
    }
    return result
  }

  const generateScheduleTimes = () => {
    const result = buildScheduleTimes()
    setGeneratedTimes(result.join('\n') || (scheduleRange?.length === 2 ? '没有生成合法时间点，请调整日期、时段或间隔' : '请先选择日期范围'))
  }

  const renderNumberWithUnit = (props, unit, width = 120) => (
    <Space.Compact>
      <InputNumber {...props} style={{ width }} />
      <Button>{unit}</Button>
    </Space.Compact>
  )

  const renderLinePool = (label, placeholder, value, onChange, order, setOrder) => (
    <Form.Item label={label}>
      <Space.Compact style={{ width: '100%' }}>
        <TextArea rows={4} value={value} onChange={event => onChange(event.target.value)} placeholder={placeholder} />
        <Select value={order} onChange={setOrder} style={{ width: 120 }} options={[{ value: 'sequential', label: '顺序使用' }, { value: 'random', label: '随机使用' }]} />
      </Space.Compact>
    </Form.Item>
  )

  const getPlatformFields = (platform) => {
    const fields = PLATFORM_FIELDS[platform] || ['标题覆盖', '正文覆盖']
    if (platform === 'xiaohongshu' && publishType === 'video') {
      return fields.filter(field => field !== '图片排序')
    }
    return fields
  }

  const renderFieldLabel = (platform, field) => {
    const requiredFields = REQUIRED_PLATFORM_FIELDS[platform] || []
    if (!requiredFields.includes(field)) return field
    return <span><span style={{ color: '#ff4d4f', marginRight: 4 }}>*</span>{field}</span>
  }

  const createPublishTasks = () => {
    const settingScope = getSettingScope()
    const pickFirstLine = (value) => String(value || '').split('\n').map(item => item.trim()).filter(Boolean)[0] || ''
    const settings = {
      sourceFolderPath, moveTargetFolderPath, afterPublishAction, timeRanges, scheduleInterval,
      titlePool, titleOrder, bodyPool, bodyOrder, topicPool, topicOrder, topicPickCount,
      locationPool, locationOrder, douyinCoverMode,
      commentPool, commentOrder, commentDelayMinutes, commentCount, commentIntervalMinutes, showPublishBrowser
    }
    if (settingScope) api.savePublishSettings(settingScope.scope, settingScope.id, settings)
    const scheduleTimes = buildScheduleTimes()
    const manualScheduleTimes = generatedTimes.split('\n').map(item => item.trim()).filter(Boolean).filter(item => /^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}$/.test(item))
    api.createPreviewPublishTasks({
      account_ids: selectedAccounts.map(account => account.account_id),
      type: publishType === 'video' ? '视频' : '图文',
      title: pickFirstLine(titlePool),
      body: pickFirstLine(bodyPool),
      topics: String(topicPool || '').split('\n').map(item => item.trim()).filter(Boolean).slice(0, topicPickCount),
      location: pickFirstLine(locationPool),
      cover_mode: douyinCoverMode,
      title_pool: String(titlePool || '').split('\n').map(item => item.trim()).filter(Boolean),
      title_order: titleOrder,
      body_pool: String(bodyPool || '').split('\n').map(item => item.trim()).filter(Boolean),
      body_order: bodyOrder,
      topic_pool: String(topicPool || '').split('\n').map(item => item.trim()).filter(Boolean),
      topic_order: topicOrder,
      topic_pick_count: topicPickCount,
      location_pool: String(locationPool || '').split('\n').map(item => item.trim()).filter(Boolean),
      location_order: locationOrder,
      comment_pool: String(commentPool || '').split('\n').map(item => item.trim()).filter(Boolean),
      comment_order: commentOrder,
      comment_delay_minutes: commentDelayMinutes,
      comment_count: commentCount,
      comment_interval_minutes: commentIntervalMinutes,
      work_count: publishMode === 'batch' ? undefined : 1,
      source_folder: sourceFolderPath,
      platform_source_folders: platformSourceFolders,
      platform_after_publish_actions: platformAfterPublishActions,
      platform_move_target_folders: platformMoveTargetFolders,
      show_publish_browser: showPublishBrowser,
      schedule_times: singleScheduleEnabled && singleScheduleTime ? [singleScheduleTime.format('YYYY-MM-DD HH:mm')] : (scheduleTimes.length > 0 ? scheduleTimes : manualScheduleTimes),
      after_publish_action: afterPublishAction,
      move_target_folder: moveTargetFolderPath,
      dry_run: false
    }).then(result => {
      if (result?.success) {
        if (result.user) {
          sessionStorage.setItem('auth_user', JSON.stringify(result.user))
          setAuthUser(result.user)
        }
        const locked = result.user?.locked_publish_platform
        message.success(locked && isBasicUser ? `发布任务已创建，基础版发布平台已绑定为${PLATFORM_NAMES[locked] || locked}` : '发布任务已创建')
      } else {
        message.error(result?.error || '创建发布任务失败')
      }
    }).catch(err => message.error(err.message || '创建发布任务失败'))
  }

  const selectFolderPath = (setter) => {
    api.selectDirectory().then(result => {
      if (!result?.canceled && result?.path) setter(result.path)
    })
  }

  const selectPlatformFolder = (platform) => {
    api.selectDirectory().then(result => {
      if (!result?.canceled && result?.path) setPlatformSourceFolders(prev => ({ ...prev, [platform]: result.path }))
    })
  }

  const renderBatchCommonSettings = () => (
    <div style={{ padding: 4 }}>
      <Alert
        type="info"
        showIcon
        message="批量发布通用设置用于生成多账号、多时间点任务；每个合法时间点可供所有目标平台各使用一次。"
        style={{ marginBottom: 16 }}
      />
      <Form layout="vertical">
        <Card size="small" title="作品来源" style={{ marginBottom: 16 }}>
          <Form.Item label="视频文件夹路径">
            <Space.Compact style={{ width: '100%' }}>
              <Input value={sourceFolderPath} onChange={event => setSourceFolderPath(event.target.value)} placeholder="选择或粘贴视频文件夹路径" />
              <Button icon={<UploadOutlined />} onClick={() => selectFolderPath(setSourceFolderPath)}>选择文件夹</Button>
            </Space.Compact>
          </Form.Item>
          <Space size="large" wrap>
            <Form.Item label="发布规则" style={{ marginBottom: 0 }}>
              <Select defaultValue="sequential" style={{ width: 140 }} options={[{ value: 'sequential', label: '顺序发布' }, { value: 'random', label: '随机发布' }]} />
            </Form.Item>
            <Form.Item label="发布后处理" style={{ marginBottom: 0 }}>
              <Select value={afterPublishAction} onChange={setAfterPublishAction} style={{ width: 140 }} options={[{ value: 'none', label: '不处理' }, { value: 'move', label: '移动到文件夹' }]} />
            </Form.Item>
            <Form.Item label="移动目标路径" style={{ marginBottom: 0 }}>
              <Space.Compact>
                <Input value={moveTargetFolderPath} onChange={event => setMoveTargetFolderPath(event.target.value)} placeholder="选择移动后的文件夹路径" style={{ width: 260 }} />
                <Button icon={<UploadOutlined />} onClick={() => selectFolderPath(setMoveTargetFolderPath)}>选择文件夹</Button>
              </Space.Compact>
            </Form.Item>
          </Space>
          <div style={{ marginTop: 12, color: '#999', fontSize: 12 }}>删除或移动会在该分组或所有账号都使用一次作品后执行；作品用完后待发任务会显示无作品。</div>
        </Card>

        <Card size="small" title="内容素材池" style={{ marginBottom: 16 }}>
          {renderLinePool('通用标题', '每行一个标题，可按顺序或随机选择使用', titlePool, setTitlePool, titleOrder, setTitleOrder)}
          {renderLinePool('通用正文', '每行一条正文，可按顺序或随机选择使用', bodyPool, setBodyPool, bodyOrder, setBodyOrder)}
          <Form.Item label="#话题设置">
            <Space.Compact style={{ width: '100%' }}>
              <TextArea rows={4} value={topicPool} onChange={event => setTopicPool(event.target.value)} placeholder="每行一个话题，例如：#新品发布" />
              <Select value={topicOrder} onChange={setTopicOrder} style={{ width: 120 }} options={[{ value: 'sequential', label: '顺序使用' }, { value: 'random', label: '随机使用' }]} />
              {renderNumberWithUnit({ min: 1, max: 20, value: topicPickCount, onChange: value => setTopicPickCount(value || 1) }, '条')}
            </Space.Compact>
          </Form.Item>
          {renderLinePool('地点位置设置', '每行一个地点位置，可按顺序或随机选择使用', locationPool, setLocationPool, locationOrder, setLocationOrder)}
        </Card>

        <Card size="small" title="定时设置" style={{ marginBottom: 16 }}>
          <Alert type="warning" showIcon message="平台定时发布时间需要在操作时 2 小时以后才算合法，生成时会自动过滤不合法时间点。" style={{ marginBottom: 12 }} />
          <Space size="large" wrap style={{ marginBottom: 12 }}>
            <Form.Item label="日期范围" style={{ marginBottom: 0 }}>
              <RangePicker value={scheduleRange} onChange={setScheduleRange} />
            </Form.Item>
            <Form.Item label="每隔多少分钟发布一条" style={{ marginBottom: 0 }}>
              {renderNumberWithUnit({ min: 1, value: scheduleInterval, onChange: value => setScheduleInterval(value || 30) }, '分钟')}
            </Form.Item>
            <Button type="primary" onClick={generateScheduleTimes}>生成时间</Button>
          </Space>
          <Form.Item label="发布时间段">
            <TextArea rows={2} value={timeRanges} onChange={event => setTimeRanges(event.target.value)} placeholder="09:20-11:50,13:50-15:00,18:00-22:00" />
          </Form.Item>
          <Form.Item label="生成后的发布时间点">
            <TextArea rows={8} value={generatedTimes} onChange={event => setGeneratedTimes(event.target.value)} placeholder="点击生成时间后在这里显示，可修改、删除或粘贴" />
          </Form.Item>
        </Card>

        <Card size="small" title="发布后追加评论">
          <Space size="large" wrap style={{ marginBottom: 12 }}>
            <Form.Item label="发布后多少分钟追加" style={{ marginBottom: 0 }}>
              {renderNumberWithUnit({ min: 0, value: commentDelayMinutes, onChange: value => setCommentDelayMinutes(value || 0) }, '分钟')}
            </Form.Item>
            <Form.Item label="追加几条" style={{ marginBottom: 0 }}>
              {renderNumberWithUnit({ min: 0, value: commentCount, onChange: value => setCommentCount(value || 0) }, '条')}
            </Form.Item>
            <Form.Item label="间隔多少分钟" style={{ marginBottom: 0 }}>
              {renderNumberWithUnit({ min: 1, value: commentIntervalMinutes, onChange: value => setCommentIntervalMinutes(value || 1) }, '分钟')}
            </Form.Item>
            <Form.Item label="使用规则" style={{ marginBottom: 0 }}>
              <Select value={commentOrder} onChange={setCommentOrder} style={{ width: 140 }} options={[{ value: 'sequential', label: '顺序使用' }, { value: 'random', label: '随机使用' }]} />
            </Form.Item>
          </Space>
          <Form.Item label="评论内容池">
            <TextArea rows={5} value={commentPool} onChange={event => setCommentPool(event.target.value)} placeholder="每行一条评论内容，按规则追加评论" />
          </Form.Item>
        </Card>
      </Form>
    </div>
  )

  const renderPlatformSettings = (platform) => {
    const fields = getPlatformFields(platform)
    return (
      <div style={{ padding: 4 }}>
        <Form layout="vertical">
          {publishMode === 'batch' && (
            <Card size="small" title="作品来源（覆盖通用设置）" style={{ marginBottom: 16 }}>
              <Alert type="info" showIcon message="留空则沿用通用设置中的作品来源文件夹" style={{ marginBottom: 8 }} />
              <Form.Item label="视频文件夹路径">
                <Space.Compact style={{ width: '100%' }}>
                  <Input value={platformSourceFolders[platform] || ''} onChange={e => setPlatformSourceFolders(prev => ({ ...prev, [platform]: e.target.value }))} placeholder="留空则沿用通用设置" />
                  <Button icon={<UploadOutlined />} onClick={() => selectPlatformFolder(platform)}>选择文件夹</Button>
                </Space.Compact>
              </Form.Item>
              <Space size="large" wrap>
                <Form.Item label="发布规则" style={{ marginBottom: 0 }}>
                  <Select defaultValue="sequential" style={{ width: 140 }} options={[{ value: 'sequential', label: '顺序发布' }, { value: 'random', label: '随机发布' }]} />
                </Form.Item>
                <Form.Item label="发布后处理" style={{ marginBottom: 0 }}>
                  <Select value={platformAfterPublishActions[platform] || ''} onChange={val => setPlatformAfterPublishActions(prev => ({ ...prev, [platform]: val }))} style={{ width: 140 }} options={[{ value: '', label: '沿用通用设置' }, { value: 'none', label: '不处理' }, { value: 'move', label: '移动到文件夹' }]} />
                </Form.Item>
                {platformAfterPublishActions[platform] === 'move' && (
                  <Form.Item label="移动目标路径" style={{ marginBottom: 0 }}>
                    <Space.Compact>
                      <Input value={platformMoveTargetFolders[platform] || ''} onChange={e => setPlatformMoveTargetFolders(prev => ({ ...prev, [platform]: e.target.value }))} placeholder="选择移动后的文件夹路径" style={{ width: 260 }} />
                      <Button icon={<UploadOutlined />} onClick={() => api.selectDirectory().then(result => { if (!result?.canceled && result?.path) setPlatformMoveTargetFolders(prev => ({ ...prev, [platform]: result.path })) })}>选择文件夹</Button>
                    </Space.Compact>
                  </Form.Item>
                )}
              </Space>
            </Card>
          )}
          {fields.map(field => {
            if (platform === 'douyin' && field === '封面方式') {
              return (
                <React.Fragment key={field}>
                  <Form.Item label={renderFieldLabel(platform, field)}>
                    <Select value={douyinCoverMode} onChange={setDouyinCoverMode} options={DOUYIN_COVER_MODE_OPTIONS} />
                  </Form.Item>
                </React.Fragment>
              )
            }
            if (platform === 'bilibili' && field === '分区') {
              return (
                <React.Fragment key={field}>
                  <Form.Item label={renderFieldLabel(platform, field)}>
                    <Select placeholder="选择分区" options={BILIBILI_SECTION_OPTIONS} showSearch filterOption={(input, option) => option.label.includes(input)} />
                  </Form.Item>
                </React.Fragment>
              )
            }
            if (platform === 'bilibili' && field === '创作声明') {
              return (
                <React.Fragment key={field}>
                  <Form.Item label={renderFieldLabel(platform, field)}>
                    <Select placeholder="选择创作声明" options={BILIBILI_DECLARATION_OPTIONS} />
                  </Form.Item>
                </React.Fragment>
              )
            }
            if (platform === 'bilibili' && field === '标签') {
              return (
                <React.Fragment key={field}>
                  <Form.Item label={renderFieldLabel(platform, field)}>
                    <Select mode="tags" placeholder="输入标签后回车" open={false} />
                  </Form.Item>
                </React.Fragment>
              )
            }
            return (
              <Form.Item key={field} label={renderFieldLabel(platform, field)}>
                {field.includes('范围') || field.includes('声明') || field.includes('分区') || field.includes('分类') ? (
                  <Select placeholder={`选择${field}`} options={[{ value: 'default', label: '默认继承通用设置' }, { value: 'custom', label: '自定义设置' }]} />
                ) : (
                  <Input placeholder={`留空则继承通用设置中的${field.replace('覆盖', '')}`} />
                )}
              </Form.Item>
            )
          })}
        </Form>
      </div>
    )
  }

  const tabItems = [
    { key: 'common', label: '通用设置', children: publishMode === 'batch' ? renderBatchCommonSettings() : renderCommonSettings() },
    ...selectedPlatforms.map(platform => ({
      key: platform,
      label: <span><span style={{ color: PLATFORM_COLORS[platform] || '#999' }}>●</span> {PLATFORM_NAMES[platform] || platform}</span>,
      children: renderPlatformSettings(platform)
    }))
  ]

  return (
    <Layout style={{ flex: 1, background: '#f5f7fb' }}>
      <Sider width={328} theme="light" style={{ borderRight: '1px solid #e8e8e8', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: 16, borderBottom: '1px solid #f0f0f0' }}>
          <Segmented
            block
            value={publishType}
            onChange={setPublishType}
            options={[
              { label: '视频发布', value: 'video', icon: <VideoCameraOutlined /> },
              { label: '图文发布', value: 'image_text', icon: <PictureOutlined /> }
            ]}
          />
          <Input
            prefix={<SearchOutlined />}
            placeholder="搜索账号或平台"
            value={keyword}
            onChange={event => setKeyword(event.target.value)}
            style={{ marginTop: 12 }}
          />
        </div>
        <div style={{ padding: '10px 16px', color: '#666', fontSize: 12, borderBottom: '1px solid #f0f0f0' }}>
          已选择 {selectedGroupIds.length} 个分组、{selectedAccounts.length} 个账号，覆盖 {selectedPlatforms.length} 个平台
        </div>
        <div style={{ flex: 1, overflow: 'auto' }}>
          {groups.length > 0 && (
            <>
              <div style={{ padding: '8px 12px', color: '#999', fontSize: 12, background: '#fafafa' }}>分组选择</div>
              {groups.map(renderGroup)}
              {standaloneAccounts.length > 0 && <div style={{ padding: '8px 12px', color: '#999', fontSize: 12, background: '#fafafa' }}>账号选择</div>}
            </>
          )}
          {standaloneAccounts.length > 0 && standaloneAccounts.map(account => renderAccount(account))}
          {groups.length === 0 && standaloneAccounts.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无账号" style={{ marginTop: 80 }} />}
        </div>
      </Sider>

      <Content style={{ padding: 18, overflow: 'auto' }}>
        <Card
          title={publishType === 'video' ? '视频发布配置' : '图文发布配置'}
          extra={(
            <Space>
              <Segmented
                value={publishMode}
                onChange={setPublishMode}
                options={[{ label: '单条发布', value: 'single' }, { label: '批量发布', value: 'batch' }]}
              />
              <Tag icon={<ClockCircleOutlined />} color="blue">支持定时发布</Tag>
              <Button>保存草稿</Button>
              <Button type="primary" icon={<SendOutlined />} onClick={createPublishTasks}>创建发布任务</Button>
            </Space>
          )}
          style={{ minHeight: '100%' }}
        >
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
            {selectedPlatforms.map(platform => (
              <Tag key={platform} color={PLATFORM_COLORS[platform]}>{PLATFORM_NAMES[platform] || platform}</Tag>
            ))}
            {selectedPlatforms.length === 0 && <Tag>请选择左侧账号生成平台设置标签</Tag>}
          </div>
          <Divider style={{ margin: '8px 0 14px' }} />
          {publishMode === 'batch' && (
            <Alert
              type="info"
              showIcon
              message="批量发布暂时复用单条发布设置界面，下一步会在这里扩展多作品编排。"
              style={{ marginBottom: 16 }}
            />
          )}
          <Tabs type="card" items={tabItems} />
        </Card>
      </Content>
    </Layout>
  )
}

export default PublishPage
